import {useEffect, useRef, useState} from 'react';
import {EventSource} from 'eventsource';
import {MERCURE_PUBLIC_URL} from '@utils';

const isMercureDebug = () => localStorage.getItem('mercureDebug') === 'true';

type UseMercureOptions = {
    topic: string;
    onMessage: (data: unknown) => void;
    enabled?: boolean;
    token?: string | null;
    onOpen?: () => void;
    onError?: (error: Event) => void;
    onClose?: () => void;
};

export const useMercure = ({
    topic,
    onMessage,
    enabled = true,
    token,
    onOpen,
    onError,
    onClose,
}: UseMercureOptions) => {
    const eventSourceRef = useRef<EventSource | null>(null);
    const [isConnected, setIsConnected] = useState(false);

    const onMessageRef = useRef(onMessage);
    const onOpenRef = useRef(onOpen);
    const onErrorRef = useRef(onError);
    const onCloseRef = useRef(onClose);

    useEffect(() => {
        onMessageRef.current = onMessage;
        onOpenRef.current = onOpen;
        onErrorRef.current = onError;
        onCloseRef.current = onClose;
    });

    useEffect(() => {
        if (!enabled || !topic) {
            return;
        }

        const mercureUrl = new URL(MERCURE_PUBLIC_URL, window.location.origin);
        mercureUrl.searchParams.append('match', topic);

        if (isMercureDebug()) {
            console.log(`🔗 Connecting to Mercure topic: ${topic}`);
        }

        const eventSource = new EventSource(mercureUrl.toString(), {
            withCredentials: true,
            ...(token && {
                fetch: (input, init) => {
                    const headers = new Headers(init.headers);
                    headers.set('Authorization', `Bearer ${token}`);
                    return fetch(input, {...init, headers});
                },
            }),
        });
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
            if (isMercureDebug()) {
                console.log(
                    `✅ [${new Date().toISOString()}] Mercure connection established for topic:`,
                    topic
                );
            }
            setIsConnected(true);
            onOpenRef.current?.();
        };

        eventSource.onmessage = event => {
            try {
                const data = JSON.parse(event.data);
                if (isMercureDebug()) {
                    console.log('📨 Mercure message received:', data);
                }
                onMessageRef.current(data);
            } catch (error) {
                console.error('❌ Failed to parse Mercure message:', error);
            }
        };

        eventSource.onerror = error => {
            if (isMercureDebug()) {
                console.error(
                    `⚠️ [${new Date().toISOString()}] Mercure connection error:`,
                    error
                );
            }
            setIsConnected(false);
            onErrorRef.current?.(error);
        };

        return () => {
            if (isMercureDebug()) {
                console.log('🔌 Closing Mercure connection for topic:', topic);
            }
            setIsConnected(false);
            onCloseRef.current?.();
            eventSource.close();
        };
    }, [topic, enabled, token]);

    return {eventSourceRef, isConnected};
};
